import React, { useEffect, useState, useCallback } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner, Table, Badge, InputGroup } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { useCookies } from 'react-cookie'
import {
  getFeeSettings,
  updateFeeSetting,
  getRewardTaxSetting,
  updateRewardTaxSetting,
  getRewardFeeSetting,
  updateRewardFeeSetting,
} from '../../../controller/api/setting'

const FEE_TYPE_LABELS = {
  FEE_VIETLOTT: 'Vietlott',
  FEE_CLASSIC: 'Xổ số truyền thống',
  FEE_LOTO: 'Lô tô',
}

const EMPTY_REWARD_FEE = {
  vietlottThreshold: '',
  vietlottPercent: '',
  classicThreshold: '',
  classicPercent: '',
  classicHighThreshold: '',
  classicHighPercent: '',
}

const formatCurrency = (value) => {
  return new Intl.NumberFormat('vi-VN').format(value)
}

const FeeSettings = () => {
  const [settings, setSettings] = useState([])
  const [endpoints, setEndpoints] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingKey, setEditingKey] = useState(null)
  const [editPercentValue, setEditPercentValue] = useState('')
  const [editFixedValue, setEditFixedValue] = useState('')
  const [saving, setSaving] = useState(false)

  const [taxThreshold, setTaxThreshold] = useState('')
  const [taxPercent, setTaxPercent] = useState('')
  const [taxLoading, setTaxLoading] = useState(false)
  const [taxSaving, setTaxSaving] = useState(false)

  const [rewardFee, setRewardFee] = useState(EMPTY_REWARD_FEE)
  const [rewardFeeLoading, setRewardFeeLoading] = useState(false)
  const [rewardFeeSaving, setRewardFeeSaving] = useState(false)

  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const fetchSettings = useCallback(async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }
    setLoading(true)
    try {
      const data = await getFeeSettings(token)
      if (data) {
        setSettings(data.settings || [])
        setEndpoints(data.endpoints || [])
      }
    } catch (error) {
      console.error('Fetch fee settings error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể tải cấu hình phí. Vui lòng thử lại.')
      }
    } finally {
      setLoading(false)
    }
  }, [token])

  const fetchTaxConfig = useCallback(async () => {
    if (!token) return
    setTaxLoading(true)
    try {
      const config = await getRewardTaxSetting(token)
      if (config) {
        setTaxThreshold(String(config.threshold))
        setTaxPercent(String(config.percent))
      }
    } catch (error) {
      console.error('Fetch reward tax setting error', error)
      toast.error('Không thể tải cấu hình thuế trúng thưởng.')
    } finally {
      setTaxLoading(false)
    }
  }, [token])

  const fetchRewardFeeConfig = useCallback(async () => {
    if (!token) return
    setRewardFeeLoading(true)
    try {
      const config = await getRewardFeeSetting(token)
      if (config) {
        setRewardFee({
          vietlottThreshold: String(config.vietlottThreshold),
          vietlottPercent: String(config.vietlottPercent),
          classicThreshold: String(config.classicThreshold),
          classicPercent: String(config.classicPercent),
          classicHighThreshold: String(config.classicHighThreshold),
          classicHighPercent: String(config.classicHighPercent),
        })
      }
    } catch (error) {
      console.error('Fetch reward fee setting error', error)
      toast.error('Không thể tải cấu hình phí trả thưởng.')
    } finally {
      setRewardFeeLoading(false)
    }
  }, [token])

  useEffect(() => {
    fetchSettings()
    fetchTaxConfig()
    fetchRewardFeeConfig()
  }, [fetchSettings, fetchTaxConfig, fetchRewardFeeConfig])

  const handleRewardFeeChange = (field, value) => {
    setRewardFee(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveRewardFee = async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ.')
      return
    }

    const payload = {
      vietlottThreshold: parseFloat(rewardFee.vietlottThreshold),
      vietlottPercent: parseFloat(rewardFee.vietlottPercent),
      classicThreshold: parseFloat(rewardFee.classicThreshold),
      classicPercent: parseFloat(rewardFee.classicPercent),
      classicHighThreshold: parseFloat(rewardFee.classicHighThreshold),
      classicHighPercent: parseFloat(rewardFee.classicHighPercent),
    }

    const thresholdFields = ['vietlottThreshold', 'classicThreshold', 'classicHighThreshold']
    if (thresholdFields.some(field => isNaN(payload[field]) || payload[field] < 0)) {
      toast.warn('Ngưỡng thu phí phải >= 0')
      return
    }

    const percentFields = ['vietlottPercent', 'classicPercent', 'classicHighPercent']
    if (percentFields.some(field => isNaN(payload[field]) || payload[field] < 0 || payload[field] > 100)) {
      toast.warn('Phí % phải từ 0 đến 100')
      return
    }

    if (payload.classicHighThreshold < payload.classicThreshold) {
      toast.warn('Ngưỡng mức 2 phải lớn hơn hoặc bằng ngưỡng mức 1')
      return
    }

    setRewardFeeSaving(true)
    try {
      const config = await updateRewardFeeSetting(token, {
        ...payload,
        vietlottThreshold: Math.floor(payload.vietlottThreshold),
        classicThreshold: Math.floor(payload.classicThreshold),
        classicHighThreshold: Math.floor(payload.classicHighThreshold),
      })
      if (config) {
        toast.success('Cập nhật phí trả thưởng thành công!')
        await fetchRewardFeeConfig()
      } else {
        toast.error('Có lỗi xảy ra khi cập nhật')
      }
    } catch (error) {
      console.error('Update reward fee setting error', error)
      toast.error('Không thể cập nhật phí trả thưởng.')
    } finally {
      setRewardFeeSaving(false)
    }
  }

  const handleSaveTax = async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ.')
      return
    }

    const threshold = parseFloat(taxThreshold)
    if (isNaN(threshold) || threshold < 0) {
      toast.warn('Ngưỡng chịu thuế phải >= 0')
      return
    }

    const percent = parseFloat(taxPercent)
    if (isNaN(percent) || percent < 0 || percent > 100) {
      toast.warn('Thuế suất phải từ 0 đến 100')
      return
    }

    setTaxSaving(true)
    try {
      const config = await updateRewardTaxSetting(token, {
        threshold: Math.floor(threshold),
        percent,
      })
      if (config) {
        setTaxThreshold(String(config.threshold))
        setTaxPercent(String(config.percent))
        toast.success('Cập nhật thuế trúng thưởng thành công!')
      } else {
        toast.error('Có lỗi xảy ra khi cập nhật')
      }
    } catch (error) {
      console.error('Update reward tax setting error', error)
      toast.error('Không thể cập nhật cấu hình thuế.')
    } finally {
      setTaxSaving(false)
    }
  }

  const handleEdit = (setting) => {
    setEditingKey(setting.key)
    setEditPercentValue(setting.percentValue || '0')
    setEditFixedValue(setting.fixedValue || '0')
  }

  const handleCancelEdit = () => {
    setEditingKey(null)
    setEditPercentValue('')
    setEditFixedValue('')
  }

  const handleSave = async (setting) => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ.')
      return
    }

    const numPercentValue = parseFloat(editPercentValue)
    if (isNaN(numPercentValue) || numPercentValue < 0 || numPercentValue > 100) {
      toast.warn('Giá trị phí % phải từ 0 đến 100')
      return
    }

    const numFixedValue = parseFloat(editFixedValue)
    if (isNaN(numFixedValue) || numFixedValue < 0) {
      toast.warn('Giá trị phí cố định phải >= 0')
      return
    }

    setSaving(true)
    try {
      const gameType = setting.feeType.replace('FEE_', '')
      const percentKey = setting.key
      const fixedKey = `FEE_FIXED_${gameType}_${setting.endPointId}`

      const result1 = await updateFeeSetting(token, {
        key: percentKey,
        value: editPercentValue,
      })

      const result2 = await updateFeeSetting(token, {
        key: fixedKey,
        value: editFixedValue,
      })
      
      if ((result1 && result1.status === 1) || (result2 && result2.status === 1)) {
        toast.success('Cập nhật phí thành công!')
        setSettings(prev => 
          prev.map(s => s.key === setting.key 
            ? { ...s, percentValue: editPercentValue, fixedValue: editFixedValue } 
            : s
          )
        )
        setEditingKey(null)
        setEditPercentValue('')
        setEditFixedValue('')
      } else {
        toast.error('Có lỗi xảy ra khi cập nhật')
      }
    } catch (error) {
      console.error('Update fee setting error', error)
      toast.error('Không thể cập nhật cấu hình phí.')
    } finally {
      setSaving(false)
    }
  }

  const groupedByEndpoint = endpoints.map(ep => ({
    endpoint: ep,
    fees: settings.filter(s => s.endPointId === ep.id)
  }))

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center mb-4">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">🧾 Thuế thu nhập cá nhân trúng thưởng</h4>
              <small className="text-muted">
                Áp dụng cho mọi loại vé: chỉ tính thuế phần giá trị vượt ngưỡng
              </small>
            </Card.Header>
            <Card.Body>
              {taxLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <Row className="align-items-end">
                    <Col md={4} className="mb-3">
                      <Form.Label>Ngưỡng chịu thuế</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={taxThreshold}
                          onChange={(e) => setTaxThreshold(e.target.value)}
                          min="0"
                          step="1000000"
                          disabled={taxSaving}
                        />
                        <InputGroup.Text>đ</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Giải thưởng từ {formatCurrency(taxThreshold || 0)}đ trở xuống không chịu thuế
                      </Form.Text>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Label>Thuế suất</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={taxPercent}
                          onChange={(e) => setTaxPercent(e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                          disabled={taxSaving}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Button variant="success" onClick={handleSaveTax} disabled={taxSaving}>
                        {taxSaving ? <Spinner animation="border" size="sm" /> : 'Lưu'}
                      </Button>
                    </Col>
                  </Row>
                  <div className="text-muted small">
                    <code>Thuế = (Giá trị trúng − Ngưỡng) × Thuế suất</code>
                    <span className="ms-2">
                      Ví dụ: trúng 30,000,000đ, ngưỡng 20,000,000đ, thuế suất 10% → Thuế = 1,000,000đ
                    </span>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="justify-content-center mb-4">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">💸 Phí trả thưởng</h4>
              <small className="text-muted">
                Phí thu trên tiền thưởng của khách, tính sau khi đã trừ thuế
              </small>
            </Card.Header>
            <Card.Body>
              {rewardFeeLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <h6 className="mb-1">Vietlott &amp; Keno</h6>
                  <p className="text-muted small">
                    Phí = (Tiền thưởng − Ngưỡng) × Phí % — chỉ thu trên phần vượt ngưỡng
                  </p>
                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Label>Ngưỡng thu phí</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={rewardFee.vietlottThreshold}
                          onChange={(e) => handleRewardFeeChange('vietlottThreshold', e.target.value)}
                          min="0"
                          step="1000000"
                          disabled={rewardFeeSaving}
                        />
                        <InputGroup.Text>đ</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Label>Phí</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={rewardFee.vietlottPercent}
                          onChange={(e) => handleRewardFeeChange('vietlottPercent', e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                          disabled={rewardFeeSaving}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </Col>
                  </Row>

                  <hr />

                  <h6 className="mb-1">Xổ số truyền thống, Lô tô &amp; Điện toán</h6>
                  <p className="text-muted small">
                    Phí = Tiền thưởng × Phí % — thu trên toàn bộ tiền thưởng, theo mức ngưỡng cao nhất đạt được
                  </p>
                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Label>Mức 1 — ngưỡng thu phí</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={rewardFee.classicThreshold}
                          onChange={(e) => handleRewardFeeChange('classicThreshold', e.target.value)}
                          min="0"
                          step="1000000"
                          disabled={rewardFeeSaving}
                        />
                        <InputGroup.Text>đ</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Label>Mức 1 — phí</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={rewardFee.classicPercent}
                          onChange={(e) => handleRewardFeeChange('classicPercent', e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                          disabled={rewardFeeSaving}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Label>Mức 2 — ngưỡng thu phí</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={rewardFee.classicHighThreshold}
                          onChange={(e) => handleRewardFeeChange('classicHighThreshold', e.target.value)}
                          min="0"
                          step="1000000"
                          disabled={rewardFeeSaving}
                        />
                        <InputGroup.Text>đ</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Label>Mức 2 — phí</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          value={rewardFee.classicHighPercent}
                          onChange={(e) => handleRewardFeeChange('classicHighPercent', e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                          disabled={rewardFeeSaving}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </Col>
                  </Row>

                  <Button variant="success" onClick={handleSaveRewardFee} disabled={rewardFeeSaving}>
                    {rewardFeeSaving ? <Spinner animation="border" size="sm" /> : 'Lưu'}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">💰 Cài đặt phí dịch vụ</h4>
                <small className="text-muted">
                  Thiết lập phí dịch vụ cho từng loại game. Tổng phí = (Giá trị × %) + Phí cố định
                </small>
              </div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={fetchSettings} 
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Tải lại'}
              </Button>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-2">Đang tải...</p>
                </div>
              ) : groupedByEndpoint.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>Chưa có cấu hình phí nào.</p>
                  <small>Vui lòng chạy seed-settings.ts để tạo cấu hình mặc định.</small>
                </div>
              ) : (
                groupedByEndpoint.map(({ endpoint, fees }) => (
                  <Card key={endpoint.id} className="mb-4">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <Badge bg="primary" className="me-2">{endpoint.id}</Badge>
                        {endpoint.name}
                      </h6>
                    </Card.Header>
                    <Table responsive className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '20%' }}>Loại game</th>
                          <th style={{ width: '15%' }}>Phí (%)</th>
                          <th style={{ width: '20%' }}>Phí cố định (VND)</th>
                          <th style={{ width: '25%' }}>Key</th>
                          <th style={{ width: '20%' }}>Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fees.map(fee => (
                          <tr key={fee.key}>
                            <td>
                              <strong>{FEE_TYPE_LABELS[fee.feeType] || fee.feeType}</strong>
                            </td>
                            <td>
                              {editingKey === fee.key ? (
                                <InputGroup size="sm" style={{ width: '100px' }}>
                                  <Form.Control
                                    type="number"
                                    value={editPercentValue}
                                    onChange={(e) => setEditPercentValue(e.target.value)}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    disabled={saving}
                                  />
                                  <InputGroup.Text>%</InputGroup.Text>
                                </InputGroup>
                              ) : (
                                <Badge bg="success">{fee.percentValue}%</Badge>
                              )}
                            </td>
                            <td>
                              {editingKey === fee.key ? (
                                <InputGroup size="sm" style={{ width: '150px' }}>
                                  <Form.Control
                                    type="number"
                                    value={editFixedValue}
                                    onChange={(e) => setEditFixedValue(e.target.value)}
                                    min="0"
                                    step="1000"
                                    disabled={saving}
                                  />
                                  <InputGroup.Text>đ</InputGroup.Text>
                                </InputGroup>
                              ) : (
                                <Badge bg="info">{formatCurrency(fee.fixedValue || 0)} đ</Badge>
                              )}
                            </td>
                            <td>
                              <code className="small">{fee.key}</code>
                            </td>
                            <td>
                              {editingKey === fee.key ? (
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleSave(fee)}
                                    disabled={saving}
                                  >
                                    {saving ? <Spinner animation="border" size="sm" /> : 'Lưu'}
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCancelEdit}
                                    disabled={saving}
                                  >
                                    Hủy
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEdit(fee)}
                                >
                                  Sửa
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card>
                ))
              )}

              <Card className="mt-4 bg-light">
                <Card.Body>
                  <h6>📌 Công thức tính phí:</h6>
                  <code>Tổng phí = (Giá trị vé × Phí %) + Phí cố định</code>
                  <p className="mt-2 mb-0 text-muted small">
                    Ví dụ: Vé 100,000đ, phí 2%, phí cố định 5,000đ → Tổng phí = (100,000 × 2%) + 5,000 = 7,000đ
                  </p>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default FeeSettings
